import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Send, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function DeskCancelCleanup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/desk-cancel-cleanup?apply=false`, {}, getAuthHeaders());
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "調査に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    if (!data || data.needs_action === 0) return;
    if (!window.confirm(`${data.needs_action}件をデスクへ取消通知します。次回のデスク取込でこれらの商品が取消されます。実行しますか？`)) {
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/desk-cancel-cleanup?apply=true`, {}, getAuthHeaders());
      toast.success(res.data.message || "処理しました");
      setData(res.data);
      // 再スキャンして残り0件を確認
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setApplying(false);
    }
  };

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="desk-cancel-cleanup">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-amber-600" />
          デスク取消クリーンアップ
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          取消済みなのにデスクへ取り込まれた（または取消がデスクへ届いていない）明細を洗い出し、まとめてデスクへ取消を通知します。
        </p>
      </div>

      {/* サマリ */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-800">対象の状況</h2>
          <Button onClick={runScan} disabled={loading} variant="outline" size="sm" data-testid="cleanup-rescan">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            再調査
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs text-slate-500">取消済み 合計</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="cleanup-total">{data?.total_cancelled ?? "-"}</div>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs text-amber-700">要対応（デスク通知が必要）</div>
            <div className="text-2xl font-bold text-amber-800" data-testid="cleanup-needs">{data?.needs_action ?? "-"}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs text-slate-500">確定扱いで流出</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="cleanup-leaked">{data?.leaked_confirmed ?? "-"}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs text-slate-500">取消通知 未記録</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="cleanup-missing">{data?.missing_tombstone ?? "-"}</div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          {data && data.needs_action === 0 ? (
            <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium" data-testid="cleanup-clean">
              <CheckCircle2 className="h-5 w-5" />
              対象はありません。デスクとの取消状態は揃っています。
            </div>
          ) : (
            <Button
              onClick={handleApply}
              disabled={applying || !data || data.needs_action === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="cleanup-apply"
            >
              <Send className="h-4 w-4 mr-2" />
              {applying ? "処理中..." : `まとめてデスクへ取消通知（${data?.needs_action ?? 0}件）`}
            </Button>
          )}
        </div>
      </div>

      {/* 一覧 */}
      {items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-800">対象明細一覧</h2>
            <span className="text-xs text-slate-500">{items.length} 件表示</span>
          </div>
          <div className="max-h-[28rem] overflow-y-auto border border-slate-100 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">店舗コード</th>
                  <th className="px-3 py-2 text-left">店舗名</th>
                  <th className="px-3 py-2 text-left">納品日</th>
                  <th className="px-3 py-2 text-left">商品名</th>
                  <th className="px-3 py-2 text-right">数量</th>
                  <th className="px-3 py-2 text-center">状態</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={`${it.type}-${it.id}`} className="border-t border-slate-100" data-testid={`cleanup-row-${idx}`}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{it.store_code}</td>
                    <td className="px-3 py-2 text-slate-800">{it.store_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{it.delivery_date}</td>
                    <td className="px-3 py-2 text-slate-800">{it.product_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(it.quantity ?? 0)} {it.unit}</td>
                    <td className="px-3 py-2 text-center">
                      {it.confirmed && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 mr-1">確定で流出</span>
                      )}
                      {!it.had_tombstone && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">未通知</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
